import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Clock, Tag, ArrowLeft, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChatWidget } from '../components/PLGComponents';
import api from '../lib/api';

const hFont = { fontFamily: 'Manrope, sans-serif' };

/* ─── Seed Articles (fallback if API has no articles) ── */
const SEED_ARTICLES = [
  {
    slug: 'ai-bookkeeping-small-business-2026',
    title: 'Why AI Bookkeeping Is No Longer Optional for Small Businesses in 2026',
    category: 'Industry Trends',
    readTime: '5 min read',
    date: '2026-03-15',
    excerpt: 'The gap between businesses using AI for their accounting and those still doing it manually is widening. Here\'s what\'s changed and why it matters.',
    content: `## The shift is real

In 2024, AI-powered bookkeeping was a novelty. In 2026, it's table stakes. Here's why:

### 1. Speed has become a competitive advantage
When your competitor can close their books in hours instead of weeks, they make better decisions faster. They see cash flow problems before they become crises. They spot profitable opportunities while they're still opportunities.

### 2. The cost equation has flipped
A typical small business owner spends 5-10 hours per month on bookkeeping. At even a modest $50/hour, that's $3,000-$6,000 per year. AI bookkeeping tools like Kaya start at free and max out at a fraction of that cost — while being more accurate.

### 3. Accuracy isn't optional anymore
Tax authorities are getting smarter. Automated reporting requirements are expanding. The margin for error is shrinking. AI doesn't get tired, doesn't skip transactions, and doesn't miscategorise expenses because it's Friday afternoon.

### What to look for in an AI bookkeeping solution

- **Bank statement processing**: It should handle PDFs, CSVs, and even photos of statements
- **Intelligent classification**: Not just rules-based, but learning from your corrections
- **Double-entry compliance**: Proper journal entries, not just expense tracking
- **Tax awareness**: Automatic VAT/GST splitting based on your jurisdiction
- **Reports that matter**: P&L, Balance Sheet, Cash Flow — not vanity dashboards

### The bottom line

If you're still manually categorising bank transactions in 2026, you're not just behind — you're overpaying for slower, less accurate results. The tools exist. The question is whether you'll adopt them now or wait until your competitor does.

*Kaya.ai processes bank statements using AI and generates complete financial reports in minutes. [Try it free →](/login)*`,
  },
  {
    slug: 'vat-compliance-freelancers-guide',
    title: 'The Freelancer\'s No-Panic Guide to VAT Compliance',
    category: 'Tax Tips',
    readTime: '7 min read',
    date: '2026-03-10',
    excerpt: 'VAT registration, filing, and tracking doesn\'t have to be stressful. Here\'s a straightforward guide for freelancers who\'d rather be doing anything else.',
    content: `## VAT: The thing nobody told you about when you went freelance

You started freelancing for the freedom. Then you crossed the VAT threshold and suddenly you're a tax collector. Fun times.

### When do you need to register?

This varies by country, but the principle is the same everywhere: once your turnover exceeds a threshold, you must register and start charging VAT on your invoices.

| Country | Threshold | Rate |
|---------|-----------|------|
| UK | £90,000 | 20% |
| South Africa | R1,000,000 | 15% |
| Germany | €22,000 | 19% |
| Australia | A$75,000 | 10% |
| Switzerland | CHF 100,000 | 8.1% |

### The 3 things you need to track

1. **Output VAT**: The tax you charge on your invoices (you collect this for the government)
2. **Input VAT**: The tax you pay on business expenses (you can claim this back)
3. **Net VAT**: Output minus Input = what you owe (or what you're owed)

### How AI makes this painless

Tools like Kaya automatically split every transaction into net amount + tax amount. When you upload your bank statement, the AI:

- Identifies which transactions include VAT
- Calculates the correct split based on your country's rates
- Generates a VAT report showing your input, output, and net position

No spreadsheets. No manual calculations. No errors.

### Pro tip: Do it monthly, not quarterly

Even if you file quarterly, reconcile monthly. It takes 5 minutes with AI tools, and you'll never have a surprise when filing day comes.

*Upload your first bank statement to Kaya and see your VAT position instantly. [Start free →](/login)*`,
  },
  {
    slug: 'read-profit-loss-statement',
    title: 'How to Read a P&L Statement (Even If You\'re Not an Accountant)',
    category: 'Financial Literacy',
    readTime: '6 min read',
    date: '2026-03-05',
    excerpt: 'Your Profit & Loss statement is the most important report in your business. Here\'s how to read it, what to look for, and what the numbers actually mean.',
    content: `## Your P&L in plain English

A Profit & Loss statement (also called an Income Statement) answers one question: **Did your business make money or lose money over a specific period?**

That's it. Everything else is detail.

### The structure

Every P&L follows the same basic structure:

**Revenue** (money coming in)
*minus* **Cost of Goods Sold** (direct costs of delivering your service/product)
*equals* **Gross Profit**
*minus* **Operating Expenses** (rent, salaries, software, travel, etc.)
*equals* **Net Profit** (the real number — what's left)

### The 3 numbers that matter most

1. **Revenue growth**: Is it going up? Month over month? Year over year? If revenue is flat or declining, nothing else matters.

2. **Gross margin**: Gross Profit ÷ Revenue × 100. This tells you how efficiently you deliver your product/service. Aim for 50%+ in services, 30%+ in product businesses.

3. **Net margin**: Net Profit ÷ Revenue × 100. This is your real profitability. Below 10%? You're working hard for thin returns. Above 20%? You're in good shape.

### Red flags to watch

- **Revenue up, profit down**: Your costs are growing faster than your income. Find out why.
- **One expense category dominating**: If any single category is more than 30% of revenue (excluding COGS), investigate.
- **Negative net profit for 3+ months**: You're burning cash. Action needed.

### Make it actionable

Don't just read your P&L. Compare it:
- This month vs last month
- This quarter vs same quarter last year
- Your margins vs industry benchmarks

The P&L is a conversation starter, not a score card. It tells you where to look, not what to do.

*Kaya generates your P&L automatically from your bank statements. Upload and see yours in minutes. [Try it free →](/login)*`,
  },
  {
    slug: 'double-entry-accounting-explained',
    title: 'Double-Entry Accounting Explained in 5 Minutes',
    category: 'Financial Literacy',
    readTime: '5 min read',
    date: '2026-02-28',
    excerpt: 'Every transaction touches two accounts. Here\'s why that matters and how AI handles it so you don\'t have to.',
    content: `## Why everything has two sides

Double-entry accounting is 500 years old. It was invented in 1494 by Luca Pacioli, and it's still the foundation of every accounting system in the world. Here's why:

**Every financial transaction affects at least two accounts.**

When you pay rent:
- Your bank account goes DOWN (credit)
- Your rent expense goes UP (debit)

When a client pays you:
- Your bank account goes UP (debit)
- Your revenue account goes UP (credit)

### The golden rule

**Debits must always equal credits.** If they don't, something is wrong.

This is why your Balance Sheet balances: Assets = Liabilities + Equity. Always. If it doesn't, there's an error somewhere.

### Why it matters for your business

1. **Accuracy**: Every transaction is recorded twice, creating a built-in error-checking system
2. **Complete picture**: You don't just know that money left your account — you know WHERE it went
3. **Audit trail**: Every movement has a paper trail through journal entries
4. **Financial reports**: P&L, Balance Sheet, Cash Flow — all generated automatically from the double-entry ledger

### The AI advantage

Manually creating journal entries for every bank transaction is tedious and error-prone. AI can:
- Read your bank statement
- Identify each transaction
- Determine the correct debit and credit accounts
- Create proper journal entries
- Post them to your general ledger

What used to take hours happens in minutes. And the AI learns from your corrections, getting more accurate over time.

*Kaya creates double-entry journal entries automatically from your bank statements. [See it in action →](/login)*`,
  },
  {
    slug: 'bookkeeper-vs-ai-accounting',
    title: '5 Signs You\'re Overpaying Your Bookkeeper',
    category: 'Industry Trends',
    readTime: '4 min read',
    date: '2026-02-20',
    excerpt: 'Your bookkeeper does valuable work. But if they\'re spending most of their time on data entry, you\'re paying professional rates for clerical work.',
    content: `## It's not about replacing people. It's about replacing tasks.

Good bookkeepers are worth their weight in gold. But here's the thing: most of what they bill you for is data entry. And that's the part AI can do in seconds.

### Sign 1: They're manually entering bank transactions

If your bookkeeper is typing transactions from your bank statement into accounting software, you're paying $30-$80/hour for copy-paste. AI does this in minutes.

### Sign 2: You wait weeks for monthly reports

If your P&L arrives 3 weeks after month-end, your bookkeeper is batch-processing. With AI, reports are available the moment transactions are posted.

### Sign 3: You find categorisation errors regularly

Humans get tired. They miscategorise. They're inconsistent. AI applies the same logic every time and learns from corrections.

### Sign 4: Your bill goes up with transaction volume

More transactions = more hours = bigger bill. With AI, processing 500 transactions costs the same as processing 50. Your costs don't scale with your success.

### Sign 5: They can't explain your numbers in real time

If you ask "How did we do last month?" and the answer is "I'll check and get back to you" — there's a problem. Your financial data should be available instantly.

### The hybrid model

The smartest approach: Use AI for the grunt work (classification, data entry, journal entries) and keep your bookkeeper or accountant for the strategic stuff (tax planning, financial advice, compliance).

You'll pay less. They'll do more valuable work. Everyone wins.

*Let Kaya handle the data entry. Your bookkeeper will thank you. [Start free →](/login)*`,
  },
];

/* ─── Blog List Page ──────────────────────────────── */
function BlogList() {
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/public/blog');
        const apiArticles = (res.data.articles || []).map(a => ({
          ...a,
          date: a.published_at || a.created_at,
          readTime: a.read_time || '5 min read',
        }));
        // Merge API articles with seed articles (API first, seeds as fallback)
        const apiSlugs = new Set(apiArticles.map(a => a.slug));
        const seeds = SEED_ARTICLES.filter(s => !apiSlugs.has(s.slug));
        setArticles([...apiArticles, ...seeds]);
      } catch {
        setArticles(SEED_ARTICLES);
      }
      setLoading(false);
    };
    load();
  }, []);

  const categories = [...new Set(articles.map(a => a.category))];
  const filtered = filter === 'All' ? articles : articles.filter(a => a.category === filter);

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 mb-5 bg-[#D94E18]/[0.06]">
          <BookOpen className="w-3.5 h-3.5 text-[#D94E18]" />
          <span className="text-xs font-medium text-[#D94E18] tracking-wide uppercase">Blog</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-zinc-900 tracking-tight" style={hFont}>
          Insights for smarter accounting
        </h1>
        <p className="text-zinc-500 mt-3 max-w-lg mx-auto">
          Practical guides on bookkeeping, tax compliance, and financial management — powered by AI.
        </p>
      </div>

      {/* Category filter */}
      <div className="flex gap-2 mb-8 flex-wrap justify-center">
        <button
          onClick={() => setFilter('All')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === 'All' ? 'bg-[#D94E18] text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
        >
          All
        </button>
        {categories.map(c => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === c ? 'bg-[#D94E18] text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Articles grid */}
      <div className="space-y-6" data-testid="blog-article-list">
        {filtered.map(article => (
          <Link key={article.slug} to={`/blog/${article.slug}`} className="block group">
            <div className="p-6 bg-white rounded-xl border border-zinc-200 hover:border-[#D94E18]/30 hover:shadow-sm transition-all">
              <div className="flex items-center gap-3 mb-3">
                <Badge variant="outline" className="text-[10px]">{article.category}</Badge>
                <span className="text-xs text-zinc-400 flex items-center gap-1"><Clock className="w-3 h-3" />{article.readTime}</span>
                <span className="text-xs text-zinc-400">{new Date(article.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <h2 className="text-lg font-bold text-zinc-900 group-hover:text-[#D94E18] transition-colors" style={hFont}>
                {article.title}
              </h2>
              <p className="text-sm text-zinc-500 mt-2 line-clamp-2">{article.excerpt}</p>
              <span className="text-sm text-[#D94E18] mt-3 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Read more <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </Link>
        ))}
      </div>

      {/* Newsletter signup */}
      <div className="mt-12 p-8 bg-zinc-900 rounded-2xl text-center" data-testid="blog-newsletter">
        <h3 className="text-xl font-bold text-white" style={hFont}>Get smarter about your numbers</h3>
        <p className="text-zinc-400 text-sm mt-2">Weekly insights on AI accounting, tax tips, and financial literacy. No spam.</p>
        <NewsletterForm />
      </div>
    </div>
  );
}

function NewsletterForm() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      const { default: api } = await import('../lib/api');
      await api.post('/public/capture-lead', { email, source: 'newsletter' });
      setSubmitted(true);
    } catch { /* silent */ }
  };

  if (submitted) return <p className="text-emerald-400 text-sm mt-4">You're in! Watch your inbox.</p>;

  return (
    <form onSubmit={handleSubmit} className="flex gap-2 max-w-sm mx-auto mt-4">
      <input
        type="email"
        placeholder="your@email.com"
        value={email}
        onChange={e => setEmail(e.target.value)}
        className="flex-1 px-3 py-2 text-sm rounded-lg bg-white/10 border border-white/10 text-white placeholder-zinc-500 focus:border-[#D94E18] outline-none"
        data-testid="newsletter-email"
      />
      <Button type="submit" className="bg-[#D94E18] hover:bg-[#C2410C] text-white" data-testid="newsletter-submit">
        Subscribe
      </Button>
    </form>
  );
}


/* ─── Single Article Page ─────────────────────────── */
function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/public/blog/${slug}`);
        setArticle({ ...res.data, date: res.data.published_at || res.data.created_at, readTime: res.data.read_time || '5 min read' });
      } catch {
        // Fallback to seed articles
        const seed = SEED_ARTICLES.find(a => a.slug === slug);
        setArticle(seed || null);
      }
      setLoading(false);
    };
    load();
  }, [slug]);

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-zinc-400">Loading...</div>;

  if (!article) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h1 className="text-2xl font-bold text-zinc-900">Article not found</h1>
        <Link to="/blog" className="text-[#D94E18] hover:underline mt-4 block">Back to blog</Link>
      </div>
    );
  }

  // Simple markdown-to-html for content
  const renderContent = (md) => {
    return md
      .split('\n')
      .map((line, i) => {
        const k = `md-${i}`;
        if (line.startsWith('## ')) return <h2 key={k} className="text-2xl font-bold text-zinc-900 mt-8 mb-4" style={hFont}>{line.slice(3)}</h2>;
        if (line.startsWith('### ')) return <h3 key={k} className="text-lg font-semibold text-zinc-800 mt-6 mb-3" style={hFont}>{line.slice(4)}</h3>;
        if (line.startsWith('- **')) {
          const parts = line.slice(2).split('**');
          return <li key={k} className="text-sm text-zinc-600 mb-2 ml-4 list-disc"><strong className="text-zinc-800">{parts[1]}</strong>{parts[2]}</li>;
        }
        if (line.startsWith('- ')) return <li key={k} className="text-sm text-zinc-600 mb-2 ml-4 list-disc">{line.slice(2)}</li>;
        if (line.startsWith('| ')) return null;
        if (line.startsWith('**') && line.endsWith('**')) return <p key={k} className="text-sm font-semibold text-zinc-800 my-3">{line.slice(2, -2)}</p>;
        if (line.startsWith('*') && line.endsWith('*') && !line.startsWith('**')) {
          const clean = line.slice(1, -1);
          if (clean.includes('[') && clean.includes('](/')) {
            const linkMatch = clean.match(/\[(.+?)\]\((.+?)\)/);
            if (linkMatch) {
              const before = clean.split('[')[0];
              return (
                <p key={k} className="text-sm text-[#D94E18] mt-6 italic">
                  {before}<Link to={linkMatch[2]} className="underline font-medium">{linkMatch[1]}</Link>
                </p>
              );
            }
          }
          return <p key={k} className="text-sm text-zinc-500 italic my-3">{clean}</p>;
        }
        if (line.trim() === '') return <div key={k} className="h-2" />;
        return <p key={k} className="text-sm text-zinc-600 leading-relaxed mb-3">{line}</p>;
      });
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid={`blog-article-${slug}`}>
      <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#D94E18] mb-8">
        <ArrowLeft className="w-4 h-4" />Back to blog
      </Link>

      <div className="flex items-center gap-3 mb-4">
        <Badge variant="outline">{article.category}</Badge>
        <span className="text-xs text-zinc-400 flex items-center gap-1"><Clock className="w-3 h-3" />{article.readTime}</span>
        <span className="text-xs text-zinc-400">{new Date(article.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
      </div>

      <h1 className="text-3xl sm:text-4xl font-extrabold text-zinc-900 tracking-tight leading-tight" style={hFont}>
        {article.title}
      </h1>
      <p className="text-base text-zinc-500 mt-4 mb-8">{article.excerpt}</p>

      <div className="prose-sm">
        {renderContent(article.content)}
      </div>

      {/* CTA */}
      <div className="mt-12 p-8 bg-[#D94E18]/5 rounded-2xl text-center border border-[#D94E18]/10">
        <h3 className="text-lg font-bold text-zinc-900" style={hFont}>Ready to try AI-powered bookkeeping?</h3>
        <p className="text-sm text-zinc-500 mt-2">Upload your first bank statement and see the magic. Free, no credit card needed.</p>
        <Link to="/login">
          <Button className="bg-[#D94E18] hover:bg-[#C2410C] text-white mt-4" data-testid="article-cta-btn">
            Get started free <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
}


/* ─── Main Blog Page ──────────────────────────────── */
export default function BlogPage() {
  const { slug } = useParams();

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="blog-page">
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-zinc-100">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-black text-zinc-900" style={hFont}>kaya<span className="text-[#D94E18]">.ai</span></Link>
          <div className="flex items-center gap-4">
            <Link to="/blog" className="text-sm text-zinc-600 hover:text-zinc-900">Blog</Link>
            <Link to="/login">
              <Button size="sm" className="bg-[#D94E18] hover:bg-[#C2410C] text-white">Try Kaya Free</Button>
            </Link>
          </div>
        </div>
      </nav>

      {slug ? <BlogArticle /> : <BlogList />}

      <ChatWidget />
    </div>
  );
}
